import type { StatusCategory } from '@jira/platform__status-categories/src/status-categories.tsx';
import { START_NODE_ID } from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';

// TODO: go/restrict-enums
// eslint-disable-next-line no-restricted-syntax
export enum TransitionIcons {
	Bolt = 'bolt',
	Error = 'error',
	Check = 'check',
	CheckInactive = 'check_inactive',
	Cross = 'cross',
	CrossInactive = 'cross_inactive',
	SharedTransition = 'shared_transition',
	TriggerAgent = 'trigger_agent',
}

export type ConditionalTransitionIcon = {
	type: TransitionIcons;
	fromStatuses?: ReadonlyArray<string>;
};

export interface Point {
	x: number;
	y: number;
}

/** PortIndeces refer to where a Port appears on a Status.
 *  These values are identical to the old draw2d workflow diagram.
 *
 * ```
 *  ╭----0----1----2----╮
 *  7       Status       3
 *  ╰----6----5----4----╯
 * ```
 */
// TODO: go/restrict-enums
// eslint-disable-next-line no-restricted-syntax
export enum PortIndex {
	/**
	 * ```txt
	 *  ╭-⭐️-----------╮
	 *  |    Status    |
	 *  ╰--------------╯
	 * ``` */
	TopLeft = 0,
	/**
	 * ```txt
	 *  ╭------⭐️------╮
	 *  |    Status    |
	 *  ╰--------------╯
	 * ``` */
	Top = 1,
	/**
	 * ```txt
	 *  ╭-----------⭐️-╮
	 *  |    Status    |
	 *  ╰--------------╯
	 * ``` */
	TopRight = 2,
	/**
	 * ```txt
	 *  ╭--------------╮
	 *  |    Status    ⭐️
	 *  ╰--------------╯
	 * ``` */
	Right = 3,
	/**
	 * ```txt
	 *  ╭--------------╮
	 *  |    Status    |
	 *  ╰-----------⭐️-╯
	 * ``` */
	BottomRight = 4,
	/**
	 * ```txt
	 *  ╭--------------╮
	 *  |    Status    |
	 *  ╰------⭐️------╯
	 * ``` */
	Bottom = 5,
	/**
	 * ```txt
	 *  ╭--------------╮
	 *  |    Status    |
	 *  ╰--⭐️----------╯
	 * ``` */
	BottomLeft = 6,
	/**
	 * ```txt
	 *  ╭--------------╮
	 *  ⭐️   Status    |
	 *  ╰--------------╯
	 * ``` */
	Left = 7,
}

export const allStatusPorts: readonly [
	PortIndex.TopLeft,
	PortIndex.Top,
	PortIndex.TopRight,
	PortIndex.Right,
	PortIndex.BottomRight,
	PortIndex.Bottom,
	PortIndex.BottomLeft,
	PortIndex.Left,
] = [
	PortIndex.TopLeft,
	PortIndex.Top,
	PortIndex.TopRight,
	PortIndex.Right,
	PortIndex.BottomRight,
	PortIndex.Bottom,
	PortIndex.BottomLeft,
	PortIndex.Left,
] as const;

export type DiagramDirectTransitionWithDefinedPorts = Omit<
	DiagramDirectTransition,
	'sourceIndex' | 'targetIndex'
> & {
	sourceIndex: PortIndex;
	targetIndex: PortIndex;
};

export type DiagramSharedTransitionWithDefinedPorts = Omit<DiagramSharedTransition, 'links'> & {
	links: {
		source: string;
		sourceIndex: PortIndex;
		targetIndex: PortIndex;
	}[];
};

/** Internally used shape for Direct Transitions which
 *  have a single source Status, and single target Status */
export type DiagramDirectTransition = {
	type: 'direct';
	id: TransitionId;
	label: string;
	flag?: ValidationFlag;
	icons: ReadonlyArray<ConditionalTransitionIcon>;
	source: string;
	sourceIndex?: PortIndex;
	target: string;
	targetIndex?: PortIndex;
};

/** Internally used shape for Shared Transitions which
 *  have many source Statuses, and single target Status */
export type DiagramSharedTransition = {
	type: 'shared';
	id: TransitionId;
	label: string;
	flag?: ValidationFlag;
	icons: ReadonlyArray<ConditionalTransitionIcon>;
	target: string;
	links: {
		source: string;
		sourceIndex?: PortIndex;
		targetIndex?: PortIndex;
	}[];
};

/** Internally used shape for Global Transitions which
 *  have a single target Status with no source Statuses */
export type DiagramGlobalTransition = {
	type: 'global';
	id: TransitionId;
	label: string;
	flag?: ValidationFlag;
	icons: ReadonlyArray<ConditionalTransitionIcon>;
	target: string;
};

/** Internally used shape for Global Looped Transitions which
 *  represent transitions between any status and itself.
 *
 *  They have no target and no sources */
export type DiagramGlobalLoopedTransition = {
	type: 'global-looped';
	id: TransitionId;
	label: string;
	flag?: ValidationFlag;
	icons: ReadonlyArray<ConditionalTransitionIcon>;
};

export type DiagramTransition =
	| DiagramDirectTransition
	| DiagramGlobalTransition
	| DiagramSharedTransition
	| DiagramGlobalLoopedTransition;

export type TransitionStateFlags = {
	isSelected: boolean;
	isTargeted: boolean;
	isHighlighted: boolean;
	isHovered: boolean;
	isRerouting: boolean;
	/** True if transition overlaps another based on criteria in `calculateOverlappingLabelPlacement` */
	isOverlapping: boolean;
	/** True if any transitions overlapping this one is being hovered */
	isOverlapHovered: boolean;
};

type DirectTransitionLayout = {
	sourcePort: PortInfo | undefined;
	targetPort: PortInfo | undefined;
	labelPosition: Point | undefined;
	path: string | undefined;
};

export type Connection = {
	sourcePort: PortInfo;
	targetPort: PortInfo;
	path: string;
	labelPosition: Point;
};

export type ConnectionPorts = {
	sourcePort: PortInfo | undefined;
	targetPort: PortInfo | undefined;
};
/**
 * Shared transitions style with connections between source to target.
 */
export type SharedTransitionLayout = {
	/** Stateful `links` */
	connections: Connection[] | undefined;
};

type GlobalTransitionLayout = {
	targetPort: PortInfo | undefined;
	labelPosition: Point | undefined;
};

type GlobalLoopedTransitionLayout = {
	labelPosition: Point | undefined;
};

export type StatefulDiagramGlobalTransition = DiagramGlobalTransition &
	TransitionStateFlags &
	GlobalTransitionLayout;

export type StatefulDiagramGlobalLoopedTransition = DiagramGlobalLoopedTransition &
	TransitionStateFlags &
	GlobalLoopedTransitionLayout;

export type StatefulDiagramDirectTransition = DiagramDirectTransition &
	TransitionStateFlags &
	DirectTransitionLayout;

/**
 * Stateful shared transition shape with generic to allow narrowing the type for the use case (which by default will be union type of all layouts - `SharedTransitionLayout`)
 *
 * ```
 * // by default, not specifying the generic parameter:
 * StatefulDiagramSharedTransition = DiagramSharedTransition & TransitionStateFlags & SharedTransitionLayout
 * ```
 */
export type StatefulDiagramSharedTransition<
	TSharedTransitionLayout extends SharedTransitionLayout = SharedTransitionLayout,
> = DiagramSharedTransition & TransitionStateFlags & TSharedTransitionLayout;

export type StatefulDiagramTransition =
	| StatefulDiagramDirectTransition
	| StatefulDiagramGlobalTransition
	| StatefulDiagramGlobalLoopedTransition
	| StatefulDiagramSharedTransition;

type StatusId = string;
type TransitionId = string;

// TODO: go/restrict-enums
// eslint-disable-next-line no-restricted-syntax
export enum ValidationFlag {
	Warn = 'warn',
	Error = 'error',
}

/** Internally used shape for Statuses */
export type DiagramStatus = {
	id: StatusId;
	label: string;
	flag?: ValidationFlag;
	position?: Point;
	category?: StatusCategory;
	isInitial?: boolean;
	showGlobalScopeIndicator: boolean;
};

export type StatefulDiagramStatus = DiagramStatus & {
	isSelected: boolean;
	isTargeted: boolean;
	isHovered: boolean;
	isMoving: boolean;
	isDragging: boolean;
};

export type StartNode = {
	id: 'START';
	position: Point | undefined;
	isTargeted: boolean;
	isMoving: boolean;
	isHovered: boolean;
	isDragging: boolean;
};

export type Size = {
	width: number;
	height: number;
};

/**
 * Describes only the layout information of a Port - the index and the position.
 * This is used where ports aren't necessarily connected to statuses
 */
export interface PortLayout {
	portIndex: PortIndex;
	position: Point;
}

/** Internally used shape for Ports that are connected to elements of the diagram */
export interface PortInfo extends PortLayout {
	/** Unique reference of a Port representing created in this format `StatusId:PortIndex` but not always (eg start node) */
	key: string;
	statusId: string;
	isVisible: boolean;
	isGlowing: boolean;
}

/** Decorated {@link DiagramTransition} with port information */
export type SelectedTransitionInfo = {
	transition: DiagramTransition;
	sourcePortIndexKeys: string[];
	targetPortIndexKeys: string[];
};

export type Line = { start: Point; end: Point };

export const toPortInfo = (
	statusId: string,
	portIndex: number,
	position: Point,
	isVisible: boolean,
	isGlowing: boolean,
): PortInfo => {
	const key = statusId === START_NODE_ID ? START_NODE_ID : `${statusId}:${portIndex}`;
	return { key, position, statusId, portIndex, isVisible, isGlowing };
};

/** Creates a key which references to a single port using status and port index which acts as a port ID */
export function toPortKey(statusId: string, portIndex: number | PortIndex): string {
	return `${statusId}:${portIndex}`;
}

/** A special {@link Point} to represent directions UP, DOWN, LEFT, RIGHT
 *  where `x` and `y` values exist in a unit circle.
 *
 * The four valid states would be
 * * Up `{ x: 0, x: 1 }`
 * * Down `{ x: 0, y: -1 }`
 * * Left `{ x: -1, y: 0 }`
 * * Right `{ x: 1, y: 0 }`
 */
export type UnitDirection = Point;

export const toPoint = (x: number, y: number): Point => ({
	x,
	y,
});

// TODO: go/restrict-enums
// eslint-disable-next-line no-restricted-syntax
export enum LayerIndex {
	/** Layer for most elements in default state */
	Default = 0,
	/** Layer for labels when in default state */
	TransitionsDefault = 1,
	/** Layer for statuses that are selected, targeted etc */
	PrioritisedStatuses = 2,
	/** Layer for transitions that are selected, rerouting, highlighted etc */
	PrioritisedTransitions = 3,
	/** Layer for overlay */
	Overlay = 4,
	/** Layer for all hovered elements */
	Hover = 5,
	/** Layer for elements that exist during the editing process */
	Proposals = 6,
	/** Layer for all Ports */
	Ports = 7,
	/** Layer for Global Loops container that sits above all other elements */
	GlobalLoopsContainer = 8,
}
